import styled from '@emotion/styled';
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';

import MemoOnIcon from '@/assets/icons/icon_memo_on.svg';
import { mq } from '@/utility/mediaQuery';
import { defaultTheme } from '@/utility/theme';
import { hoverOpacity } from '@/utility/theme/animation';

export const RaceLink = styled.div(
  (props: { isPast?: boolean; selected?: boolean }) =>
    mq({
      display: 'flex',
      flex: '0 0 auto',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%',
      height: '100%',
      fontSize: defaultTheme.font.size.m,
      fontWeight: props.selected ? 'bold' : 'normal',
      backgroundColor: props.isPast
        ? defaultTheme.background.mediumLightGray
        : defaultTheme.background.white,
      border: `${props.selected ? '2px' : '1px'} solid ${
        props.selected
          ? defaultTheme.border.color.black
          : defaultTheme.border.color.mediumGray
      }`,
      borderRadius: '8px',
      marginRight: defaultTheme.spacing.xs,
    }),
);

export const RaceCancel = styled.div(() =>
  mq({
    display: 'flex',
    flex: '0 0 auto',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    width: '100%',
    height: '100%',
    color: defaultTheme.text.gray,
    fontSize: defaultTheme.font.size.m,
    backgroundColor: defaultTheme.border.color.mediumGray,
    borderRadius: defaultTheme.border.radius.s,
  }),
);

export const StyledLink = styled(Link)(() =>
  mq({
    display: 'block',
    width: '100%',
    height: '100%',
    ...hoverOpacity,
    '& > div': {
      width: '100%',
      height: '100%',
    },
  }),
);

export const MemoOn = styled.i(() =>
  mq({
    position: 'relative',
    top: '-18px',
    marginRight: defaultTheme.spacing.xs,
    marginLeft: 'auto',
    display: 'block',
    width: '12px',
    height: '14px',
    cursor: 'pointer',
    ...hoverOpacity,
    background: `url(${MemoOnIcon}) no-repeat center`,
  }),
);

export const DisabledLink = styled.div(() =>
  mq({
    color: defaultTheme.color.secondary,
    fontSize: defaultTheme.font.size.m,
    textAlign: 'center',
    border: `1px solid ${defaultTheme.color.secondary}`,
    borderRadius: defaultTheme.border.radius.s,
    opacity: defaultTheme.button.opacity.disabled,
    padding: `${defaultTheme.spacing.m} 0`,
    '& > span': {
      marginLeft: defaultTheme.spacing.xs,
    },
  }),
);

interface RaceStatusLinkProps {
  raceNum: number;
  to: string;
  isPast?: boolean;
  isSelected?: boolean;
  isCanceled?: boolean;
  isFuture?: boolean;
  getCommentCount?: (raceId: string) => boolean;
  raceId?: string;
}

// レースのEntryStatusによって見た目が変わるリンク
const RaceButton: React.FC<RaceStatusLinkProps> = ({
  raceNum,
  to,
  isPast,
  isSelected,
  isCanceled,
  isFuture,
  raceId,
  getCommentCount,
}) => {
  const hasComment = useMemo(() => {
    if (getCommentCount && raceId) {
      return getCommentCount(raceId);
    }
    return false;
  }, [raceId, getCommentCount]);

  if (isFuture) {
    return <DisabledLink data-testid="disabled">{raceNum}R</DisabledLink>;
  }
  if (isCanceled) {
    return <RaceCancel data-testid="cancel">{raceNum}R 中止</RaceCancel>;
  }
  return (
    <StyledLink
      to={{
        pathname: to,
      }}
      state={{ cancelScroll: true }}
    >
      <RaceLink isPast={isPast} selected={isSelected} data-testid="link">
        {raceNum}R
      </RaceLink>
      {hasComment ? <MemoOn /> : <></>}
    </StyledLink>
  );
};

export default RaceButton;
